import React, { useState } from 'react';
import { PrinterIcon } from '@heroicons/react/24/outline';
import PermissionsContainer from '../PermissionsContainer';
import printingService, { type PrinterType, type PrintOptions } from '../../services/printing.service';
import PrintDialog from './PrintDialog';

export const PRINT_SEND_COMPONENT_ID = 'print-send';
export const PRINT_SEND_COMPONENT_NAME = 'Print — Send To Printer';

export interface PrintButtonProps {
  /** Full relative path of the page's `POST .../print` route. */
  endpoint: string;
  type?: PrinterType;
  /** Extra JSON merged into the print request body (e.g. invoice ids, `qr`). */
  extraBody?: Record<string, unknown>;
  label?: string;
  title?: string;
  iconOnly?: boolean;
  className?: string;
  disabled?: boolean;
  /** Called before the dialog opens; return false to block (e.g. "not dispatched yet"). */
  onBeforeOpen?: () => boolean | void;
}

/**
 * The one Print control. Hidden until the `print-send` component is granted
 * (defaultHidden), opens PrintDialog, posts to the page's print route.
 */
const PrintButton: React.FC<PrintButtonProps> = ({
  endpoint, type = 'LASER', extraBody, label = 'Print', title, iconOnly = false,
  className = '', disabled = false, onBeforeOpen,
}) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    if (onBeforeOpen && onBeforeOpen() === false) return;
    setOpen(true);
  };

  const onPrint = (opts: Required<PrintOptions>) => printingService.printTo(endpoint, opts, extraBody);

  return (
    <PermissionsContainer
      componentId={PRINT_SEND_COMPONENT_ID}
      componentName={PRINT_SEND_COMPONENT_NAME}
      defaultHidden
      showHandle={false}
    >
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        title={title ?? label}
        aria-label={label}
        className={className || (iconOnly
          ? 'inline-flex items-center justify-center w-7 h-7 rounded hover:bg-slate-100 dark:hover:bg-gray-700 disabled:opacity-40'
          : 'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-theme-border-primary text-body hover:bg-theme-bg-tertiary disabled:opacity-50')}
      >
        <PrinterIcon className="w-4 h-4" aria-hidden="true" />
        {!iconOnly && <span>{label}</span>}
      </button>
      {/* Mounted only while open, so a page with many buttons does not pay for a
          defaults subscription and a toast-context read per button. */}
      {open && (
        <PrintDialog open type={type} title={title ?? label} onClose={() => setOpen(false)} onPrint={onPrint} />
      )}
    </PermissionsContainer>
  );
};

export default PrintButton;
